import { notEmpty, type Optional } from '@festina-life/core'
import { useCallback, useEffect, useRef, useState } from 'react'

export const localStorageEvent = (key: string) => `storage-${key}`

type SetLocalStorageValue<T> = (nextValue: Optional<T>) => void

const readTypedValueFromLocalStorage = <T>(key?: string) => {
  if (!key) {
    return null
  }
  const json = localStorage.getItem(key)

  try {
    return json ? (JSON.parse(json) as T) : null
  } catch {
    return null
  }
}

export function useLocalStorage<T>(
  key: string,
  fallbackValue: T,
): readonly [T, SetLocalStorageValue<T>]
export function useLocalStorage<T>(
  key?: string,
  fallbackValue?: T,
): readonly [T | undefined, SetLocalStorageValue<T>]
export function useLocalStorage<T>(key?: string, fallbackValue?: T) {
  const savedValue = readTypedValueFromLocalStorage<T>(key)
  const fallbackRef = useRef(fallbackValue)
  fallbackRef.current = fallbackValue
  const [value, setValue] = useState<T | null>(
    savedValue ?? fallbackValue ?? null,
  )

  useEffect(() => {
    if (key) {
      const listenStorageChange = () => {
        const nextValue = readTypedValueFromLocalStorage<T>(key)
        setValue(nextValue ?? fallbackRef.current ?? null)
      }
      window.addEventListener(localStorageEvent(key), listenStorageChange)

      listenStorageChange()

      return () =>
        window.removeEventListener(localStorageEvent(key), listenStorageChange)
    }

    return undefined // No key provided, but ts complains if we don't return something for all paths
  }, [key])

  const set = useCallback(
    (nextValue: Optional<T>) => {
      if (key) {
        localStorage.setItem(
          key,
          notEmpty(nextValue) ? JSON.stringify(nextValue) : '',
        )
        setValue(nextValue ?? null)
        window.dispatchEvent(new Event(localStorageEvent(key)))
      }
    },
    [key],
  )

  return [
    value ?? undefined, // remove null to allow consumers to set fallback values more easily
    set,
  ] as const
}
